import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Crown, Sparkles, Zap, ShieldCheck, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, formatPrice } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function Membership() {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get("/plans");
        if (mounted) setPlans(r.data);
      } catch (e) { console.error(e); }
    })();
    return () => { mounted = false; };
  }, []);

  const subscribe = async (plan) => {
    if (!user) { navigate(`/login?next=/membership`); return; }
    setBusy(plan.id);
    try {
      const { data } = await api.post("/checkout/plan", { plan_id: plan.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen pb-24 bg-[#F8FAFC]" data-testid="membership-page">
      {/* Cinematic header banner */}
      <section className="relative overflow-hidden bg-[#0F0F14] text-white">
        <img src="/brand/gameflow-banner.jpg" alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-50" style={{ animation: "kenburns 40s ease-in-out infinite alternate" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F0F14]/60 via-[#0F0F14]/55 to-[#0F0F14]" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-[#A855F7]/35 blur-[120px] rounded-full" />
        <div className="relative gf-container py-20 lg:py-28 text-center">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-xs font-medium text-[#FBBF24] mb-6">
              <Crown className="w-3.5 h-3.5" /> GAMEFLOW PREMIUM
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter">
              <span className="block text-gradient-purple neon-text">UNLOCK MORE</span>
              <span className="block">PLAY MORE</span>
            </h1>
            <p className="mt-5 text-base lg:text-lg text-slate-300 max-w-xl mx-auto">
              Exclusive deals, bigger discounts, free monthly drops and priority support — built for true gamers.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="gf-container -mt-20 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {plans.map((p, i) => {
            const isPopular = p.popular;
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className={`relative rounded-3xl p-7 ${isPopular ? "bg-gradient-to-br from-[#1F1F2E] via-[#171723] to-[#0F0F14] text-white border-2 border-[#A855F7] shadow-[0_24px_60px_rgba(168,85,247,0.4)] md:scale-105" : "bg-white border border-slate-200"}`}
                data-testid={`plan-card-${p.name.toLowerCase()}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#A855F7] text-white text-[11px] font-bold tracking-wider shadow-neon-soft uppercase">Most popular</div>
                )}
                <div className="flex items-baseline justify-between">
                  <h3 className={`font-display text-2xl font-bold ${isPopular ? "text-white" : "text-slate-900"}`}>{p.name}</h3>
                  {p.color === "gold" && <Crown className="w-5 h-5 text-[#FBBF24]" />}
                  {p.color === "purple" && <Sparkles className="w-5 h-5 text-[#A855F7]" />}
                  {p.color === "violet" && <Zap className="w-5 h-5 text-[#C084FC]" />}
                </div>
                <p className={`text-sm mt-1 ${isPopular ? "text-slate-300" : "text-slate-500"}`}>{p.tagline}</p>

                <div className="mt-5 flex items-baseline gap-2">
                  <span className={`font-display text-4xl font-bold ${isPopular ? "text-white" : "text-slate-900"}`}>{formatPrice(p.price, lang)}</span>
                  <span className={`text-sm ${isPopular ? "text-slate-400" : "text-slate-500"}`}>/ {p.period === "year" ? "year" : p.period === "lifetime" ? "lifetime" : "month"}</span>
                </div>

                <Button
                  onClick={() => subscribe(p)}
                  disabled={busy === p.id}
                  className={`mt-6 w-full h-11 rounded-xl font-semibold ${isPopular ? "btn-neon" : "bg-slate-900 hover:bg-slate-800 text-white"}`}
                  data-testid={`subscribe-${p.name.toLowerCase()}`}
                >
                  {busy === p.id ? "…" : <>Subscribe now <ArrowRight className="w-4 h-4 ml-2" /></>}
                </Button>

                <ul className="mt-6 space-y-3">
                  {p.features.map((f, k) => (
                    <li key={k} className={`flex items-start gap-2.5 text-sm ${isPopular ? "text-slate-200" : "text-slate-700"}`}>
                      <Check className={`w-4 h-4 shrink-0 mt-0.5 ${isPopular ? "text-[#C084FC]" : "text-[#A855F7]"}`} />
                      {f}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="gf-container py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Zap, t: "Instant Activation", s: "Membership benefits unlock right after payment confirmation." },
            { icon: ShieldCheck, t: "Cancel Anytime", s: "No long contracts. Cancel from your dashboard whenever you want." },
            { icon: Crown, t: "Members-only Drops", s: "Get early access to keys & flash deals before everyone else." },
          ].map((b, i) => (
            <div key={i} className="rounded-2xl bg-white border border-slate-200 p-6">
              <div className="w-10 h-10 rounded-xl bg-[#A855F7]/10 grid place-items-center text-[#A855F7] mb-3"><b.icon className="w-5 h-5" /></div>
              <div className="font-display font-bold text-slate-900">{b.t}</div>
              <p className="text-sm text-slate-500 mt-1">{b.s}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
